import {testIdProps} from '@github-ui/test-id-props'
import {IssueOpenedIcon, RepoIcon, TriangleDownIcon} from '@primer/octicons-react'
import {
  Button,
  SelectPanel,
  type SelectPanelItemInput as ItemInput,
  type SelectPanelItemProps as ItemProps,
} from '@primer/react'
import debounce, {type DebouncedFunc} from 'lodash-es/debounce'
import {useCallback, useEffect, useRef, useState} from 'react'

import {apiGetRepositories} from '../../api/repository/api-get-repositories'
import {apiSearchRepositories} from '../../api/repository/api-search-repositories'
import type {SuggestedRepository} from '../../api/repository/contracts'
import {getInitialState} from '../../helpers/initial-state'
import {useProjectNumber} from '../../state-providers/memex/use-project-number'
import {Resources, WorkflowResources} from '../../strings'
import {RepositoryIcon} from '../fields/repository/repository-icon'
import {buildPickerLabel} from './helpers/accessible-label'
import {SEARCH_DEBOUNCE_DELAY_MS} from './helpers/search-constants'

type AgentRepositoryPickerProps = {
  /** Called when the user selects a repository. */
  onRepositorySelected: (repo: SuggestedRepository) => void
  /** Called when the user clears their repository selection (back to "Set by issue"). */
  onRepositoryCleared: () => void
  /** The NWO of the currently selected repo, or undefined for "Set by issue". */
  selectedRepoNwo?: string
  isEditing?: boolean
  headerDescription?: string
}

const getRepoName = (repo: SuggestedRepository) => {
  const {projectOwner} = getInitialState()
  const repoOwner = repo.nameWithOwner.split('/')[0]
  return repoOwner?.toLowerCase() !== projectOwner?.login.toLowerCase() ? repo.nameWithOwner : repo.name
}

const SET_BY_ISSUE_ID = '__set_by_issue__'

/**
 * A repo picker for auto-assign agent that supports an "unset" state ("Set by issue").
 * Unlike the standard RepositoryPicker which always auto-selects a repo, this one
 * lets the user opt out and fall back to issue-based repo resolution.
 */
export const AgentRepositoryPicker = ({
  onRepositorySelected,
  onRepositoryCleared,
  selectedRepoNwo,
  isEditing,
  headerDescription,
}: AgentRepositoryPickerProps) => {
  const [query, setQuery] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [repositories, setRepositories] = useState<Array<SuggestedRepository>>([])
  const [suggestedRepositories, setSuggestedRepositories] = useState<Array<SuggestedRepository>>([])
  const debouncedSearchRef = useRef<DebouncedFunc<() => Promise<void>> | undefined>(undefined)
  const {projectNumber} = useProjectNumber()
  const loadingRef = useRef(false)

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    if (isOpen && suggestedRepositories.length === 0 && !loadingRef.current) {
      const fetchSuggestedRepos = async () => {
        loadingRef.current = true
        setLoading(true)

        try {
          const suggestedRepos = await apiGetRepositories({memexNumber: projectNumber})
          setSuggestedRepositories(suggestedRepos.repositories)
        } finally {
          loadingRef.current = false
          setLoading(false)
        }
      }

      void fetchSuggestedRepos()
    }
  }, [isOpen, projectNumber, suggestedRepositories.length])

  const fetchRepos = useCallback(async (searchQuery: string) => {
    if (searchQuery !== '') {
      setLoading(true)
      try {
        const {repositories: searchedRepos} = await apiSearchRepositories({query: searchQuery})
        setRepositories(searchedRepos)
      } finally {
        setLoading(false)
      }
    } else {
      setRepositories([])
    }
  }, [])

  const searchRepos = useCallback(
    (value: string) => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel()
      }
      debouncedSearchRef.current = debounce(() => fetchRepos(value), SEARCH_DEBOUNCE_DELAY_MS)
      debouncedSearchRef.current()
    },
    [fetchRepos],
  )

  const onSearchChange = (value: string) => {
    setQuery(value)
    searchRepos(value)
  }

  const displayedRepos = query !== '' ? repositories : suggestedRepositories

  const clearRepository = useCallback(() => {
    onRepositoryCleared()
    onClose()
  }, [onClose, onRepositoryCleared])

  const selectRepository = useCallback(
    (repo: SuggestedRepository) => {
      onRepositorySelected(repo)
      onClose()
    },
    [onClose, onRepositorySelected],
  )

  const setByIssueItem: ItemProps = {
    id: SET_BY_ISSUE_ID,
    text: WorkflowResources.assignCopilotRepositorySetByIssueLabel,
    leadingVisual: () => <IssueOpenedIcon size={16} />,
    selected: undefined,
    onAction: (_, event) => {
      event.preventDefault()
      clearRepository()
    },
  }

  const repoItems: Array<ItemProps> = displayedRepos.map(repoItem => {
    const name = getRepoName(repoItem)
    return {
      id: repoItem.id,
      text: name,
      leadingVisual: () => RepositoryIcon({repository: repoItem}),
      selected: undefined,
      onAction: (_, event) => {
        event.preventDefault()
        selectRepository(repoItem)
      },
    }
  })

  // Show "Set by issue" at the top when not actively searching
  const displayedItems: Array<ItemProps> = query !== '' ? repoItems : [setByIssueItem, ...repoItems]

  const onSelectedChange = (selectedItem?: ItemInput) => {
    if (!selectedItem) return

    if (selectedItem.id === SET_BY_ISSUE_ID) {
      clearRepository()
    } else {
      const repo = displayedRepos.find(r => r.id === selectedItem.id)
      if (repo) selectRepository(repo)
    }
  }

  const matchedRepo = selectedRepoNwo ? displayedRepos.find(r => r.nameWithOwner === selectedRepoNwo) : undefined

  const selectedPanelItem: ItemInput = selectedRepoNwo
    ? matchedRepo
      ? {id: matchedRepo.id, text: selectedRepoNwo}
      : {id: selectedRepoNwo, text: selectedRepoNwo}
    : {id: SET_BY_ISSUE_ID, text: WorkflowResources.assignCopilotRepositorySetByIssueLabel}

  const label = buildPickerLabel(headerDescription, selectedRepoNwo)

  const buttonLabel = selectedRepoNwo || WorkflowResources.assignCopilotRepositorySetByIssueLabel

  return (
    <SelectPanel
      placeholderText={Resources.repoPickerFilterPlaceholder}
      open={isOpen}
      title={Resources.repoListTitle}
      renderAnchor={({children, ...anchorProps}) => (
        <Button
          disabled={!isEditing}
          leadingVisual={RepoIcon}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
          {...testIdProps('workflow-auto-assign-agent-repository')}
          aria-label={label}
        >
          {children || buttonLabel}
        </Button>
      )}
      onOpenChange={open => setIsOpen(open)}
      loading={loading}
      selected={selectedPanelItem}
      filterValue={query}
      items={displayedItems}
      showItemDividers
      onFilterChange={onSearchChange}
      onSelectedChange={onSelectedChange}
      message={
        displayedItems.length === 0 && !loading
          ? {
              title: Resources.repoPickerNoneFoundTitle,
              body: Resources.repoPickerNoneFoundBody,
              variant: 'empty' as const,
            }
          : undefined
      }
      overlayProps={{
        width: 'small',
        onMouseDown: e => e.stopPropagation(),
        height: 'auto',
        onClickOutside: onClose,
        ...testIdProps('workflow-auto-assign-agent-repository-panel'),
      }}
    />
  )
}

AgentRepositoryPicker.displayName = 'AgentRepositoryPicker'
